import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Logo from '../ui/Logo';
import Icon from '../ui/Icons';
import COMPANY, { ROUTES } from '../../config/company';

const NAV = [
  ['Home', ROUTES.home],
  ['Services', ROUTES.services],
  ['About', ROUTES.about],
  ['SMS Updates', ROUTES.smsOptIn],
  ['Contact', ROUTES.contact],
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const linkClass = ({ isActive }) =>
    `relative px-3 py-2 text-[0.9rem] font-semibold tracking-wide transition-colors ${
      isActive ? 'text-gold-300' : 'text-white/85 hover:text-white'
    } after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[2px] after:origin-left after:scale-x-0 after:bg-gold-300 after:transition-transform ${
      isActive ? 'after:scale-x-100' : 'hover:after:scale-x-100'
    }`;

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      {/* Top utility bar */}
      <div
        className={`hidden bg-navy-950 text-[0.78rem] text-navy-100 transition-all duration-300 md:block ${
          scrolled ? 'max-h-0 overflow-hidden opacity-0' : 'max-h-12 opacity-100'
        }`}
      >
        <div className="shell flex h-9 items-center justify-between">
          <div className="flex items-center gap-6">
            {COMPANY.phone && <a href={COMPANY.phone} className="inline-flex items-center gap-1.5 hover:text-gold-300">
              <Icon name="phone" className="h-3.5 w-3.5" /> {COMPANY.phoneDisplay}
            </a>}
            <a href={COMPANY.email} className="inline-flex items-center gap-1.5 hover:text-gold-300">
              <Icon name="mail" className="h-3.5 w-3.5" /> {COMPANY.emailDisplay}
            </a>
          </div>
          <div className="inline-flex items-center gap-1.5">
            <Icon name="clock" className="h-3.5 w-3.5" /> {COMPANY.hours}
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div
        className={`border-b transition-all duration-300 ${
          scrolled ? 'border-white/10 bg-navy-900/95 shadow-lift backdrop-blur' : 'border-transparent bg-navy-900'
        }`}
      >
        <div className="shell flex h-[4.5rem] items-center justify-between gap-4">
          <Link to={ROUTES.home} aria-label={`${COMPANY.brand} home`}>
            <Logo inverse />
          </Link>

          <nav className="hidden items-center lg:flex" aria-label="Main">
            {NAV.map(([label, to]) => (
              <NavLink key={to} to={to} className={linkClass} end={to === ROUTES.home}>
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <Link to={ROUTES.contact} className="btn-primary px-5 py-2.5 text-[0.85rem]">
              Get a quote
              <Icon name="arrowRight" className="h-4 w-4" />
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-white hover:bg-white/10 lg:hidden"
            aria-expanded={open}
            aria-label={open ? 'Close menu' : 'Open menu'}
          >
            <Icon name={open ? 'x' : 'menu'} className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden ${open ? 'pointer-events-auto' : 'pointer-events-none'}`}
        aria-hidden={!open}
      >
        <div
          className={`fixed inset-0 top-[4.5rem] bg-navy-950/60 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setOpen(false)}
        />
        <nav
          className={`absolute inset-x-0 top-full origin-top bg-navy-900 px-5 pb-8 pt-3 shadow-lift transition-all duration-200 ${
            open ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
          }`}
          aria-label="Mobile"
        >
          <ul className="divide-y divide-white/10">
            {NAV.map(([label, to]) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={to === ROUTES.home}
                  className={({ isActive }) =>
                    `flex items-center justify-between py-3.5 font-display text-xl font-bold ${
                      isActive ? 'text-gold-300' : 'text-white'
                    }`
                  }
                >
                  {label}
                  <Icon name="arrowRight" className="h-4 w-4 opacity-60" />
                </NavLink>
              </li>
            ))}
          </ul>
          <Link to={ROUTES.contact} className="btn-primary mt-5 w-full">
            Get a quote
          </Link>
          <div className="mt-5 flex flex-col gap-2 text-[0.9rem] text-navy-100">
            {COMPANY.phone && <a href={COMPANY.phone} className="inline-flex items-center gap-2">
              <Icon name="phone" className="h-4 w-4 text-gold-300" /> {COMPANY.phoneDisplay}
            </a>}
            <a href={COMPANY.email} className="inline-flex items-center gap-2">
              <Icon name="mail" className="h-4 w-4 text-gold-300" /> {COMPANY.emailDisplay}
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
}
